import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';

import useFormValidation from 'hooks/useFormValidation';

import { STATE_LIST } from 'constants/address';
import { CURRENCY_LIST } from 'constants/currency';
import { VENDOR_VALIDATION_SCHEMA } from 'constants/vendor';

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'false' },
];

const VendorForm = ({ vendor, onSubmit, onCancel, isLoading }) => {
  const initialValues = {
    // General Details
    name: vendor?.name || '',
    corporation_number: vendor?.corporation_number || '',
    business_registration_number: vendor?.business_registration_number || '',
    // Contact Details
    contact_name: vendor?.contact_name || '',
    contact_email: vendor?.contact_email || '',
    contact_phone: vendor?.contact_phone || '',
    // Accounting Details
    pan: vendor?.pan || '',
    tin: vendor?.tin || '',
    gstin: vendor?.gstin || '',
    currency: vendor?.currency || '',
    is_tax_free: vendor?.is_tax_free?.toString() || '',
    is_accepts_cform: vendor?.is_accepts_cform?.toString() || '',
    is_tcs_addition_enable: vendor?.is_tcs_addition_enable?.toString() || '',
    purchase_expiry_period: vendor?.purchase_expiry_period || '',
    // Billing Address
    contact_address_line_1: vendor?.contact_address_line_1 || '',
    contact_address_line_2: vendor?.contact_address_line_2 || '',
    contact_country: vendor?.contact_country || '',
    contact_pincode: vendor?.contact_pincode || '',
    contact_state: vendor?.contact_state || '',
    contact_city: vendor?.contact_city || '',
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    isValid,
  } = useFormValidation(initialValues, VENDOR_VALIDATION_SCHEMA);

  const handleSubmitForm = e => {
    e.preventDefault();

    if (!validateForm()) return;

    const filteredValues = Object.entries(values).reduce(
      (acc, [key, value]) => {
        if (value === '') {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    onSubmit(filteredValues);
  };

  return (
    <Form onSubmit={handleSubmitForm}>
      {/* General Details Section */}
      <div className="mb-4 border-bottom border-dotted">
        <h5 className="mb-3 fs-9">General Details</h5>
        <p className="text-muted small mb-3">
          Enter basic details of the vendor.
        </p>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Vendor Code(Auto Filled) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control placeholder="#0000" disabled={true} />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Vendor Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={values.name}
                onChange={e => handleChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                isInvalid={touched.name && errors.name}
                placeholder="Vendor Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Corporation Number</Form.Label>
              <Form.Control
                type="text"
                name="corporation_number"
                value={values.corporation_number}
                onChange={e =>
                  handleChange('corporation_number', e.target.value)
                }
                placeholder="Corporation Number"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Business Registration Number</Form.Label>
              <Form.Control
                type="text"
                name="business_registration_number"
                value={values.business_registration_number}
                onChange={e =>
                  handleChange('business_registration_number', e.target.value)
                }
                placeholder="Business Registration Number"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Contact Details Section */}
      <div className="mb-4 border-bottom border-dotted">
        <h5 className="mb-3 fs-9">Contact Details</h5>
        <p className="text-muted small mb-3">
          Name and other details of the vendor contact.
        </p>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Contact Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_name"
                value={values.contact_name}
                onChange={e => handleChange('contact_name', e.target.value)}
                onBlur={() => handleBlur('contact_name')}
                isInvalid={touched.contact_name && errors.contact_name}
                placeholder="Contact Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Email ID <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                name="contact_email"
                value={values.contact_email}
                onChange={e => handleChange('contact_email', e.target.value)}
                onBlur={() => handleBlur('contact_email')}
                isInvalid={touched.contact_email && errors.contact_email}
                placeholder="johnsmith@example.com"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Phone Number <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_phone"
                value={values.contact_phone}
                onChange={e => handleChange('contact_phone', e.target.value)}
                onBlur={() => handleBlur('contact_phone')}
                isInvalid={touched.contact_phone && errors.contact_phone}
                placeholder="9800000000"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_phone}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Accounting Details Section */}
      <div className="mb-4 border-bottom border-dotted">
        <h5 className="mb-3 fs-9">Accounting Details</h5>
        <p className="text-muted small mb-3">
          Specify accounting details of the vendor for tax and invoicing
          purposes.
        </p>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                PAN <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="pan"
                value={values.pan}
                onChange={e => handleChange('pan', e.target.value)}
                onBlur={() => handleBlur('pan')}
                isInvalid={touched.pan && errors.pan}
                placeholder="ABCD1234"
              />
              <Form.Control.Feedback type="invalid">
                {errors.pan}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>TIN</Form.Label>
              <Form.Control
                type="text"
                name="tin"
                value={values.tin}
                onChange={e => handleChange('tin', e.target.value)}
                placeholder="ABCD1234"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                GSTIN <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="gstin"
                value={values.gstin}
                onChange={e => handleChange('gstin', e.target.value)}
                onBlur={() => handleBlur('gstin')}
                isInvalid={touched.gstin && errors.gstin}
                placeholder="ABCD1234"
              />
              <Form.Control.Feedback type="invalid">
                {errors.gstin}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Currency <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="currency"
                value={values.currency}
                onChange={e => handleChange('currency', e.target.value)}
                onBlur={() => handleBlur('currency')}
                isInvalid={touched.currency && errors.currency}
              >
                <option value="">Select...</option>
                {CURRENCY_LIST.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.currency}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Tax Exempted</Form.Label>
              <Form.Select
                name="is_tax_free"
                value={values.is_tax_free}
                onChange={e => handleChange('is_tax_free', e.target.value)}
              >
                <option value="">Select...</option>
                {BOOLEAN_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Accepts CForm</Form.Label>
              <Form.Select
                name="is_accepts_cform"
                value={values.is_accepts_cform}
                onChange={e => handleChange('is_accepts_cform', e.target.value)}
              >
                <option value="">Select...</option>
                {BOOLEAN_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>TCS Addition Enabled</Form.Label>
              <Form.Select
                name="is_tcs_addition_enable"
                value={values.is_tcs_addition_enable}
                onChange={e =>
                  handleChange('is_tcs_addition_enable', e.target.value)
                }
              >
                <option value="">Select...</option>
                {BOOLEAN_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Purchase Expiry Period</Form.Label>
              <Form.Control
                type="text"
                name="purchase_expiry_period"
                value={values.purchase_expiry_period}
                onChange={e =>
                  handleChange('purchase_expiry_period', e.target.value)
                }
                placeholder="ABCD1234"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Billing Address Section */}
      <div className="mb-4">
        <h5 className="mb-3 fs-9">Billing Address</h5>
        <p className="text-muted small mb-3">
          Configure the billing address of the vendor.
        </p>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Address Line 1 <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_address_line_1"
                value={values.contact_address_line_1}
                onChange={e =>
                  handleChange('contact_address_line_1', e.target.value)
                }
                onBlur={() => handleBlur('contact_address_line_1')}
                isInvalid={
                  touched.contact_address_line_1 &&
                  errors.contact_address_line_1
                }
                placeholder="LandMark 01"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_address_line_1}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                name="contact_address_line_2"
                value={values.contact_address_line_2}
                onChange={e =>
                  handleChange('contact_address_line_2', e.target.value)
                }
                placeholder="Plot No. 1"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Country <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_country"
                value={values.contact_country}
                onChange={e => handleChange('contact_country', e.target.value)}
                onBlur={() => handleBlur('contact_country')}
                isInvalid={touched.contact_country && errors.contact_country}
                placeholder="India"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_country}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                Pin Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_pincode"
                value={values.contact_pincode}
                onChange={e => handleChange('contact_pincode', e.target.value)}
                onBlur={() => handleBlur('contact_pincode')}
                isInvalid={touched.contact_pincode && errors.contact_pincode}
                placeholder="Enter Pin Code"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_pincode}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                State <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="contact_state"
                value={values.contact_state}
                onChange={e => handleChange('contact_state', e.target.value)}
                onBlur={() => handleBlur('contact_state')}
                isInvalid={touched.contact_state && errors.contact_state}
              >
                <option value="">Select...</option>
                {STATE_LIST.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.contact_state}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_city"
                value={values.contact_city}
                onChange={e => handleChange('contact_city', e.target.value)}
                onBlur={() => handleBlur('contact_city')}
                isInvalid={touched.contact_city && errors.contact_city}
                placeholder="Delhi"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_city}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="d-flex gap-2 mt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={!isValid || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>

        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default VendorForm;
